import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CourseRecommendations Component
 * AI-powered course recommendations based on student progress
 */
function CourseRecommendations({ user, onSelectCourse }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  useEffect(() => {
    fetchRecommendations();
  }, []);
  
  const fetchRecommendations = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/recommendations/courses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRecommendations(response.data.recommendations || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading recommendations");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div style={{
        background: "#1a1a2a",
        borderRadius: "15px",
        padding: "30px",
        textAlign: "center"
      }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>🎯</div>
        <p style={{ color: "#aaa" }}>Analyzing your progress...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        background: "#1a1a2a",
        borderRadius: "15px",
        padding: "30px",
        textAlign: "center"
      }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>📚</div>
        <p style={{ color: "#aaa" }}>{error}</p>
        <button
          onClick={fetchRecommendations}
          style={{
            background: "#FFD700",
            color: "#000",
            border: "none",
            borderRadius: "20px",
            padding: "10px 20px",
            marginTop: "15px",
            cursor: "pointer"
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div style={{
      background: "linear-gradient(135deg, #1a1a2a 0%, #2a1a3a 100%)",
      borderRadius: "20px",
      padding: "25px",
      border: "2px solid #FFD700"
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "15px",
        marginBottom: "25px"
      }}>
        <div style={{
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.5rem"
        }}>
          🎯
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#FFD700", fontSize: "1.3rem" }}>
            Recommended For You
          </h3>
          <p style={{ margin: 0, color: "#aaa", fontSize: "0.9rem" }}>
            Based on your learning progress
          </p>
        </div>
      </div>
      
      {/* Recommendations */}
      {recommendations.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ color: "#aaa" }}>
            Complete some courses to get personalized recommendations!
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {recommendations.map((rec, index) => (
            <div
              key={index}
              onClick={() => onSelectCourse && onSelectCourse(rec)}
              style={{
                background: "#0a0a0a",
                borderRadius: "12px",
                padding: "20px",
                cursor: "pointer",
                border: "1px solid #333",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "flex-start",
                gap: "15px"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#FFD700";
                e.currentTarget.style.transform = "translateX(5px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#333";
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              {/* Rank Badge */}
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: index === 0 
                  ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                  : index === 1 
                    ? "linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)"
                    : "#333",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: index < 2 ? "#000" : "#fff",
                fontWeight: "bold",
                fontSize: "1.1rem",
                flexShrink: 0
              }}>
                {index + 1}
              </div>
              
              {/* Content */}
              <div style={{ flex: 1 }}>
                <h4 style={{
                  margin: "0 0 8px 0",
                  color: "#fff",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  {rec.subject_icon && <span>{rec.subject_icon}</span>}
                  {rec.subject_name}
                </h4>
                
                <p style={{
                  margin: "0 0 10px 0",
                  color: "#aaa",
                  fontSize: "0.9rem",
                  lineHeight: "1.5"
                }}>
                  {rec.reason}
                </p>
                
                <div style={{
                  display: "flex",
                  gap: "15px",
                  flexWrap: "wrap"
                }}>
                  {/* Grade Level */}
                  <span style={{
                    background: "#1a2a1a",
                    color: "#90EE90",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    fontSize: "0.8rem"
                  }}>
                    {rec.grade_level}
                  </span>
                  
                  {/* Match Score */}
                  <span style={{
                    background: "#2a1a1a",
                    color: "#FFD700",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    fontSize: "0.8rem"
                  }}>
                    {rec.match_score}% Match
                  </span>
                  
                  {/* Type */}
                  {rec.type && (
                    <span style={{
                      background: "#1a1a2a",
                      color: "#87CEEB",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "0.8rem"
                    }}>
                      {rec.type}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Arrow */}
              <div style={{
                color: "#FFD700",
                fontSize: "1.5rem",
                alignSelf: "center"
              }}>
                →
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Refresh Button */}
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={fetchRecommendations}
          style={{
            background: "transparent",
            color: "#FFD700",
            border: "1px solid #FFD700",
            borderRadius: "20px",
            padding: "8px 20px",
            cursor: "pointer",
            fontSize: "0.9rem"
          }}
        >
          🔄 Refresh Recommendations
        </button>
      </div>
    </div>
  );
}

export default CourseRecommendations;
